import axios from "axios";
import { z } from "zod";

import { computePdChecksum } from "@/src/lib/ehr/ecw/webecw/crypto";
import { getEcwAxiosProxy } from "@/src/lib/ehr/ecw/webecw/egress-proxy";
import {
  classifyEcwValidationFailure,
  EcwChecksumError,
  classifyEcwResponseBody,
  EcwError,
  EcwHttpStatusError,
  EcwOperationFailedError,
  EcwResponseParseReason,
  EcwResponseParseError,
  EcwResponseParseStage,
  EcwSessionDeadError,
  EcwSessionDeadReason,
  summarizeEcwValidationIssues,
  type EcwResponseShape,
  type EcwValidationIssue,
} from "@/src/lib/ehr/ecw/webecw/errors";
import { withEcwRequestMetric } from "@/src/lib/ehr/ecw/webecw/metrics";
import { type EcwMultipartPart, serializeMultipart } from "@/src/lib/ehr/ecw/webecw/multipart";
import type { EcwSession } from "@/src/lib/ehr/ecw/webecw/session";
import { EcwSoapParseError, parseSoapEnvelope } from "@/src/lib/ehr/ecw/webecw/xml";

export enum EcwHttpMethod {
  GET = "GET",
  POST = "POST",
}

enum EcwAccept {
  JSON = "application/json, text/plain, */*",
  XML = "text/xml, application/xml, */*",
}

enum EcwContentType {
  FORM = "application/x-www-form-urlencoded; charset=UTF-8",
  JSON = "application/json",
}

enum EcwEmptyBodyHandling {
  SESSION_DEAD = "session-dead",
  PASS_TO_PARSER = "pass-to-parser",
}

const SESSION_DEAD_REDIRECT_MARKERS = ["microsoftonline.com", "/saml", "newLogin.jsp"];
const LOGIN_PAGE_BODY_MARKERS = ["loginfmt", 'name="otc"', "<!DOCTYPE html>"];

export function locationLooksLikeSamlBounce(location: string | null): boolean {
  if (!location) return false;

  return SESSION_DEAD_REDIRECT_MARKERS.some((m) => location.includes(m));
}

function bodyLooksLikeLoginPage(rawText: string): boolean {
  return LOGIN_PAGE_BODY_MARKERS.some((m) => rawText.includes(m));
}

function bodyIsEmpty(rawText: string): boolean {
  return rawText.trim().length === 0;
}

interface ResponseParseDetails {
  parseReason: EcwResponseParseReason;
  parseStage: EcwResponseParseStage;
  responseShape?: EcwResponseShape;
  validationIssues?: EcwValidationIssue[];
}

function responseParseDetails(text: string, details: ResponseParseDetails) {
  return {
    ...details,
    responseBodyKind: classifyEcwResponseBody(text),
    responseBytes: Buffer.byteLength(text, "utf8"),
  };
}

interface EcwRequestBase {
  path: string;
  timeoutMs?: number;
}

type EcwQuery = Record<string, string>;

type EncodedBody = { data: string; contentType: EcwContentType } | { data: Buffer; contentType: string };

export type EcwRequest<B> =
  | (EcwRequestBase & { method: EcwHttpMethod.GET; query: EcwQuery })
  | (EcwRequestBase & { method: EcwHttpMethod.POST; body: B })
  | (EcwRequestBase & { method: EcwHttpMethod.POST; query: EcwQuery })
  | (EcwRequestBase & { method: EcwHttpMethod.POST; query: EcwQuery; body: B });

type EcwDialect<B> = {
  accept: EcwAccept;
  emptyBodyHandling: EcwEmptyBodyHandling;
  encodeBody(body: B): EncodedBody;
  finalizeQuery(request: EcwRequest<B>, baseQuery: string, body: string): string;
};

function standardContextTuple(session: EcwSession): Record<string, string> {
  return {
    sessionDID: session.trUserId,
    TrUserId: session.trUserId,
    Device: "webemr",
    ecwappprocessid: "0",
    timestamp: String(Date.now()),
    clientTimezone: "America/New_York",
  };
}

function appendChecksumQuery<B>(request: EcwRequest<B>, baseQuery: string, body: string): string {
  const checksumKey = request.method === EcwHttpMethod.GET ? "gd" : "pd";

  let checksum: string;

  try {
    checksum = computePdChecksum(baseQuery, body);
  } catch (e) {
    throw new EcwChecksumError(request.path, { cause: e });
  }

  return `${baseQuery}&${checksumKey}=${checksum}`;
}

const encodeFormBody = (body: Record<string, string>): EncodedBody => ({
  data: new URLSearchParams(body).toString(),
  contentType: EcwContentType.FORM,
});

const JSON_DIALECT: EcwDialect<Record<string, unknown>> = {
  accept: EcwAccept.JSON,
  emptyBodyHandling: EcwEmptyBodyHandling.SESSION_DEAD,
  encodeBody: (body) => ({ data: JSON.stringify(body), contentType: EcwContentType.JSON }),
  finalizeQuery: appendChecksumQuery,
};

const FORM_JSON_DIALECT: EcwDialect<Record<string, string>> = {
  accept: EcwAccept.JSON,
  emptyBodyHandling: EcwEmptyBodyHandling.SESSION_DEAD,
  encodeBody: encodeFormBody,
  finalizeQuery: appendChecksumQuery,
};

const EMPTY_FORM_DIALECT: EcwDialect<Record<string, string>> = {
  ...FORM_JSON_DIALECT,
  emptyBodyHandling: EcwEmptyBodyHandling.PASS_TO_PARSER,
};

const SOAP_DIALECT: EcwDialect<Record<string, string>> = {
  accept: EcwAccept.XML,
  emptyBodyHandling: EcwEmptyBodyHandling.SESSION_DEAD,
  encodeBody: encodeFormBody,
  finalizeQuery: appendChecksumQuery,
};

const UPLOAD_DIALECT: EcwDialect<EcwMultipartPart[]> = {
  accept: EcwAccept.JSON,
  emptyBodyHandling: EcwEmptyBodyHandling.SESSION_DEAD,
  encodeBody: (parts) => {
    const { data, contentTypeHeader } = serializeMultipart(parts);

    return { data, contentType: contentTypeHeader };
  },
  finalizeQuery: (_request, baseQuery) => baseQuery,
};

/** zlib failure codes for a response body that is not valid compressed data. */
const DECOMPRESSION_ERROR_CODES = new Set(["Z_BUF_ERROR", "Z_DATA_ERROR"]);

/**
 * Runs an eCW request, converting a response-decompression failure into the status error the
 * response itself would have produced.
 */
async function requestWithDecodedStatus<T>(path: string, send: () => Promise<T>): Promise<T> {
  try {
    return await send();
  } catch (error) {
    const code = (error as { code?: unknown })?.code;

    if (typeof code !== "string" || !DECOMPRESSION_ERROR_CODES.has(code)) throw error;

    const { status, response } = error as { status?: unknown; response?: { status?: unknown } };
    const resolved =
      typeof status === "number" ? status : typeof response?.status === "number" ? response.status : null;

    if (resolved === null) throw error;

    throw new EcwHttpStatusError(resolved, path);
  }
}

async function executeEcwRequest<B>(
  session: EcwSession,
  request: EcwRequest<B>,
  dialect: EcwDialect<B>
): Promise<string> {
  const baseQuery = new URLSearchParams({
    ...("query" in request ? request.query : {}),
    ...standardContextTuple(session),
  }).toString();

  const encoded: EncodedBody | null = "body" in request ? dialect.encodeBody(request.body) : null;
  const checksumBody = encoded && typeof encoded.data === "string" ? encoded.data : "";
  const url = `${session.ecwHost}${request.path}?${dialect.finalizeQuery(request, baseQuery, checksumBody)}`;

  const headers: Record<string, string> = {
    "Cookie": session.cookieHeader,
    [session.csrfHeader]: session.csrfToken,
    "X-Requested-With": "XMLHttpRequest",
    "isajaxrequest": "true",
    "Accept": dialect.accept,
  };

  if (encoded) {
    headers["Content-Type"] = encoded.contentType;
  }

  const data: string | Buffer | undefined = request.method === EcwHttpMethod.POST ? (encoded?.data ?? "") : undefined;

  const response = await requestWithDecodedStatus(request.path, () =>
    axios.request<string>({
      method: request.method,
      url,
      headers,
      data,
      ...(request.timeoutMs !== undefined ? { timeout: request.timeoutMs } : {}),
      proxy: getEcwAxiosProxy(session.ecwHost),
      maxRedirects: 0,
      validateStatus: () => true,
      responseType: "text",
      transformResponse: (d) => d,
    })
  );

  const rawText = response.data;

  const locationHeader = response.headers["location"];
  const location = typeof locationHeader === "string" ? locationHeader : "";

  if (response.status === 302 && locationLooksLikeSamlBounce(location)) {
    throw new EcwSessionDeadError(
      EcwSessionDeadReason.REDIRECT_SAML,
      `eCW session bounced to login: ${location.slice(0, 200)}`
    );
  }

  if (response.status === 401) {
    throw new EcwSessionDeadError(EcwSessionDeadReason.STATUS_401, `eCW endpoint ${request.path} returned 401`);
  }

  if (response.status === 403) {
    throw new EcwSessionDeadError(EcwSessionDeadReason.STATUS_403, `eCW endpoint ${request.path} returned 403`);
  }

  if (response.status !== 200) {
    throw new EcwHttpStatusError(response.status, request.path, classifyEcwResponseBody(rawText));
  }

  if (bodyLooksLikeLoginPage(rawText)) {
    throw new EcwSessionDeadError(EcwSessionDeadReason.PARSE_HTML_LOGIN, `eCW returned login HTML for ${request.path}`);
  }

  if (dialect.emptyBodyHandling === EcwEmptyBodyHandling.SESSION_DEAD && bodyIsEmpty(rawText)) {
    throw new EcwSessionDeadError(
      EcwSessionDeadReason.PARSE_NOT_JSON,
      `eCW returned an empty body for ${request.path}`
    );
  }

  return rawText;
}

export function callEcwSoapEndpoint<S extends z.ZodTypeAny>(
  session: EcwSession,
  request: EcwRequest<Record<string, string>>,
  schema: S
): Promise<z.infer<S>> {
  return withEcwRequestMetric({ path: request.path, groupId: session.groupId, method: request.method }, async () => {
    const text = await executeEcwRequest(session, request, SOAP_DIALECT);

    try {
      return parseSoapEnvelope(text, schema);
    } catch (e) {
      if (e instanceof EcwError) throw e;

      throw new EcwResponseParseError(request.path, 200, {
        ...responseParseDetails(
          text,
          e instanceof EcwSoapParseError
            ? {
                parseReason: e.parseReason,
                parseStage: e.parseStage,
                responseShape: e.responseShape,
                validationIssues: e.validationIssues,
              }
            : {
                parseReason: classifyEcwValidationFailure(e),
                parseStage: EcwResponseParseStage.RESPONSE_SCHEMA,
                validationIssues: summarizeEcwValidationIssues(e),
              }
        ),
      });
    }
  });
}

export function throwIfResponseIndicatesSessionDeath(opts: {
  text: string;
  reason: EcwSessionDeadReason;
  message: string;
}): void {
  if (!/JSONObject text must begin with/.test(opts.text)) {
    return;
  }

  throw new EcwSessionDeadError(opts.reason, opts.message);
}

export function parseEcwJson(text: string, path: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    throwIfResponseIndicatesSessionDeath({
      text,
      reason: EcwSessionDeadReason.PARSE_NOT_JSON,
      message: `eCW returned an org.json error body for ${path}`,
    });

    throw new EcwResponseParseError(path, 200, {
      ...responseParseDetails(text, {
        parseReason: EcwResponseParseReason.INVALID_JSON,
        parseStage: EcwResponseParseStage.JSON_DECODE,
      }),
    });
  }
}

export function callEcwJsonEndpoint<T>(
  session: EcwSession,
  request: EcwRequest<Record<string, unknown>>,
  parse: (json: unknown) => T
): Promise<T> {
  return withEcwRequestMetric({ path: request.path, groupId: session.groupId, method: request.method }, async () => {
    const text = await executeEcwRequest(session, request, JSON_DIALECT);
    const json = parseEcwJson(text, request.path);

    try {
      return parse(json);
    } catch (e) {
      if (e instanceof EcwError) throw e;

      throw new EcwResponseParseError(request.path, 200, {
        ...responseParseDetails(text, {
          parseReason: classifyEcwValidationFailure(e),
          parseStage: EcwResponseParseStage.RESPONSE_SCHEMA,
          validationIssues: summarizeEcwValidationIssues(e),
        }),
      });
    }
  });
}

function callEcwFormEndpointWithDialect<T>(
  session: EcwSession,
  request: EcwRequest<Record<string, string>>,
  parse: (text: string) => T,
  dialect: EcwDialect<Record<string, string>>
): Promise<T> {
  return withEcwRequestMetric({ path: request.path, groupId: session.groupId, method: request.method }, async () => {
    const text = await executeEcwRequest(session, request, dialect);

    try {
      return parse(text);
    } catch (e) {
      if (e instanceof EcwError) throw e;

      throw new EcwResponseParseError(request.path, 200, {
        ...responseParseDetails(text, {
          parseReason: classifyEcwValidationFailure(e),
          parseStage: EcwResponseParseStage.RESPONSE_BODY,
          validationIssues: summarizeEcwValidationIssues(e),
        }),
      });
    }
  });
}

export function callEcwFormEndpoint<T>(
  session: EcwSession,
  request: EcwRequest<Record<string, string>>,
  parse: (text: string) => T
): Promise<T> {
  return callEcwFormEndpointWithDialect(session, request, parse, FORM_JSON_DIALECT);
}

export function callEcwEmptyFormEndpoint(
  session: EcwSession,
  request: EcwRequest<Record<string, string>>
): Promise<void> {
  return callEcwFormEndpointWithDialect(
    session,
    request,
    (text) => {
      if (!bodyIsEmpty(text)) {
        throw new EcwOperationFailedError(request.path, "expected an empty response body");
      }
    },
    EMPTY_FORM_DIALECT
  );
}

export function callEcwUploadEndpoint<T>(
  session: EcwSession,
  request: EcwRequest<EcwMultipartPart[]>,
  parse: (text: string) => T
): Promise<T> {
  return withEcwRequestMetric({ path: request.path, groupId: session.groupId, method: request.method }, async () => {
    const text = await executeEcwRequest(session, request, UPLOAD_DIALECT);

    try {
      return parse(text);
    } catch (e) {
      if (e instanceof EcwError) throw e;

      throw new EcwResponseParseError(request.path, 200, {
        ...responseParseDetails(text, {
          parseReason: classifyEcwValidationFailure(e),
          parseStage: EcwResponseParseStage.RESPONSE_BODY,
          validationIssues: summarizeEcwValidationIssues(e),
        }),
      });
    }
  });
}
